import React, { useState, useEffect } from 'react';
import { MessageCircle, Target, Heart, Brain, Zap, Download, Volume2, VolumeX, Loader2 } from 'lucide-react';

// Use production API when deployed, localhost for development
const API_URL = import.meta.env.VITE_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:8787' 
    : 'https://cf-api.b-9f2.workers.dev');

const SelfTalkAI = () => {
  const [currentCategory, setCurrentCategory] = useState('motivation');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [savedMessages, setSavedMessages] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [error, setError] = useState(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  // Categories for different types of self-talk
  const categories = {
    motivation: {
      icon: <Zap className="w-5 h-5" />,
      name: 'Motivation',
      color: 'from-orange-500 to-red-500',
      prompts: [
        'I need motivation to start my day productively',
        'Help me push through a challenging project',
        'I want to feel energized about my goals',
        'Give me strength to overcome procrastination'
      ]
    },
    confidence: {
      icon: <Target className="w-5 h-5" />,
      name: 'Confidence',
      color: 'from-blue-500 to-purple-500',
      prompts: [
        'I need to believe in my abilities',
        'Help me prepare for an important presentation',
        'I want to feel confident in social situations',
        'Remind me of my strengths and accomplishments'
      ]
    },
    wellness: {
      icon: <Heart className="w-5 h-5" />,
      name: 'Wellness',
      color: 'from-green-500 to-teal-500',
      prompts: [
        'I need encouragement for self-care',
        'Help me maintain work-life balance',
        'I want to practice gratitude and mindfulness',
        'Remind me to be kind to myself'
      ]
    },
    growth: {
      icon: <Brain className="w-5 h-5" />,
      name: 'Growth',
      color: 'from-purple-500 to-pink-500',
      prompts: [
        'I want to embrace learning and challenges',
        'Help me see setbacks as opportunities',
        'I need perspective on my personal development',
        'Encourage me to step out of my comfort zone'
      ]
    }
  };

  // Get user ID from localStorage (if authenticated)
  const getUserId = () => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        return JSON.parse(user).id;
      } catch {
        return null;
      }
    }
    return null;
  };

  // Load saved messages
  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    const userId = getUserId();
    
    // If user is authenticated, fetch from backend
    if (userId) {
      setIsLoadingMessages(true);
      try {
        const response = await fetch(`${API_URL}/api/selftalk/messages`, {
          headers: {
            'X-User-Id': userId.toString()
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setSavedMessages(data.messages || []);
        }
      } catch (error) {
        console.error('Failed to load messages from server:', error);
        // Fall back to localStorage
        loadLocalMessages();
      } finally {
        setIsLoadingMessages(false);
      }
    } else {
      // Not authenticated, use localStorage
      loadLocalMessages();
    }
  };

  const loadLocalMessages = () => {
    const saved = localStorage.getItem('selftalk-messages');
    if (saved) {
      try {
        setSavedMessages(JSON.parse(saved));
      } catch {
        setSavedMessages([]);
      }
    }
  };

  // Save message
  const saveMessage = async (message) => {
    const userId = getUserId();
    const newMessage = {
      id: Date.now(),
      content: message,
      message: message,
      category: currentCategory,
      prompt: customPrompt,
      created_at: new Date().toISOString()
    };

    // If authenticated, save to backend
    if (userId) {
      try {
        const response = await fetch(`${API_URL}/api/selftalk/messages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId.toString()
          },
          body: JSON.stringify({
            category: currentCategory,
            prompt: customPrompt,
            message: message
          })
        });

        if (response.ok) {
          const data = await response.json();
          newMessage.id = data.id;
        }
      } catch (error) {
        console.error('Failed to save to server:', error);
      }
    }

    // Always save to localStorage as backup
    const updated = [newMessage, ...savedMessages].slice(0, 50);
    setSavedMessages(updated);
    localStorage.setItem('selftalk-messages', JSON.stringify(updated));
  };

  // Generate content using backend API
  const generateContent = async (prompt) => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const userId = getUserId();
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (userId) {
        headers['X-User-Id'] = userId.toString();
      }

      const response = await fetch(`${API_URL}/api/selftalk/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt,
          category: categories[currentCategory].name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate message');
      }

      const data = await response.json();
      const content = data.message;
      setGeneratedContent(content);
      
      // Text-to-speech
      if (voiceEnabled && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(content);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        speechSynthesis.speak(utterance);
      }
    } catch (error) {
      console.error('Generation error:', error);
      setError('Failed to generate message. Please try again.');
      
      // Fallback message
      const fallbackMessages = {
        motivation: "You have the drive within you to accomplish amazing things today. Take one small step forward, and momentum will follow.",
        confidence: "Your unique perspective and skills are exactly what's needed right now. Trust yourself - you're more capable than you realize.",
        wellness: "Taking care of yourself isn't selfish, it's necessary. You deserve the same compassion you show others.",
        growth: "Every challenge you face is sculpting you into a stronger version of yourself. Embrace the learning opportunity before you."
      };
      
      const fallback = fallbackMessages[currentCategory];
      setGeneratedContent(fallback);
      
      if (voiceEnabled && 'speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(fallback);
        utterance.rate = 0.9;
        speechSynthesis.speak(utterance);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerate = () => {
    const prompt = customPrompt || categories[currentCategory].prompts[Math.floor(Math.random() * categories[currentCategory].prompts.length)];
    generateContent(prompt);
  };

  const handleSave = () => {
    if (generatedContent) {
      saveMessage(generatedContent);
    }
  };

  const exportMessages = () => {
    const dataStr = JSON.stringify(savedMessages, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'selftalk-messages.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen text-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            SelfTalk AI
          </h1>
          <p className="text-slate-300">Generate personalized motivation and encouragement</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {/* Category Selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(categories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setCurrentCategory(key)}
              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                currentCategory === key
                  ? 'border-purple-400 bg-purple-500/20'
                  : 'border-slate-600 bg-slate-800/50 hover:border-slate-500'
              }`}
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-r ${category.color} mb-2`}>
                {category.icon}
              </div>
              <div className="font-semibold text-sm">{category.name}</div>
            </button>
          ))}
        </div>

        {/* Main Interface */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                What do you need to hear?
              </h2>
              
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder={`Example: ${categories[currentCategory].prompts[0]}`}
                className="w-full h-32 bg-slate-900/50 border border-slate-600 rounded-lg p-4 text-white placeholder-slate-400 focus:outline-none focus:border-purple-400 resize-none"
              />

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    'Generate Message'
                  )}
                </button>
                
                <button
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                  className={`px-4 py-3 rounded-lg border transition-all duration-200 ${
                    voiceEnabled 
                      ? 'border-purple-400 bg-purple-500/20 text-purple-300' 
                      : 'border-slate-600 bg-slate-800/50 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
                </button>
              </div>

              {/* Quick Prompts */}
              <div className="mt-4">
                <p className="text-sm text-slate-400 mb-2">Quick prompts:</p>
                <div className="space-y-1">
                  {categories[currentCategory].prompts.slice(0, 3).map((prompt, index) => (
                    <button
                      key={index}
                      onClick={() => setCustomPrompt(prompt)}
                      className="block w-full text-left text-sm text-slate-300 hover:text-white bg-slate-700/30 hover:bg-slate-700/50 px-3 py-2 rounded transition-colors"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="space-y-6">
            {/* Generated Content */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h2 className="text-xl font-semibold mb-4">Your Message</h2>
              
              {generatedContent ? (
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4">
                    <p className="text-lg leading-relaxed">{generatedContent}</p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleSave}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Save Message
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-slate-400">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Your personalized message will appear here</p>
                </div>
              )}
            </div>

            {/* Saved Messages */}
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Saved Messages</h2>
                {savedMessages.length > 0 && (
                  <button
                    onClick={exportMessages}
                    className="flex items-center gap-2 text-sm bg-slate-700 hover:bg-slate-600 px-3 py-1 rounded transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                )}
              </div>
              
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                  </div>
                ) : savedMessages.length > 0 ? (
                  savedMessages.slice(0, 10).map((message) => (
                    <div key={message.id} className="bg-slate-700/30 rounded-lg p-3">
                      <p className="text-sm mb-2">{message.message || message.content}</p>
                      <div className="flex justify-between text-xs text-slate-400">
                        <span className="capitalize">{message.category}</span>
                        <span>{new Date(message.created_at || message.timestamp).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-slate-400 text-center py-4">No saved messages yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfTalkAI;